import React, { useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Upload, Button, message, Space, Typography } from "antd";

const { Dragger } = Upload;
const { Text } = Typography;

export default function FileUploadHybrid({
  name = "file",
  allowedFormats = [".xlsx"],   // default: .xlsx
  maxFileBytes = 20 * 1024 * 1024, // default: 20 MB
  accept,                       // optional accept string for input (defaults to allowedFormats)
  onSelect,                     // callback(file) when user selects a file
  onUpload,                     // callback(file) when user presses Upload
  onRemove,                     // callback(file) when user removes the file
  uploadButtonText = "Upload"
  // label for Upload button
}) {
  const [fileList, setFileList] = useState([]); // holds at most one File
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // build accept string if not passed
  const acceptStr = accept || allowedFormats.join(",");

  const validateFile = (file) => {
    const name = (file.name || "").toLowerCase();

    // extension check
    const extOk = allowedFormats.some((ext) => name.endsWith(ext.toLowerCase()));
    if (!extOk) {
      message.error(`Invalid file type. Allowed: ${allowedFormats.join(", ")}`);
      return false;
    }

    // size check
    if (file.size > maxFileBytes) {
      message.error(`File too large. Max allowed size is ${(maxFileBytes / 1024 / 1024).toFixed(2)} MB.`);
      return false;
    }

    return true;
  };

  // intercept before upload: save file to state and prevent auto upload by returning false
  const beforeUpload = (file) => {
    if (!validateFile(file)) return Upload.LIST_IGNORE;
    // keep single file only
    setFileList([file]);
    setSelectedFile(file);
    if (onSelect) onSelect(file);
    // prevent antd from auto uploading
    return false;
  };

  const handleRemove = (file) => {
    setFileList([]);
    setSelectedFile(null);
    if (onRemove) onRemove(file);
    return true;
  };

  // When user presses Upload button, just call the onUpload callback and let caller handle actual upload.
  const handleStartUpload = async () => {
    if (!selectedFile) {
      message.warn("Please select one file before uploading.");
      return;
    }
    try {
      setUploading(true);
      // Caller handles network/upload; we just pass the File object.
      // Allow the callback to return a promise if they want to await it.
      if (onUpload) {
        await onUpload(selectedFile);
      } else {
        message.info("No onUpload callback provided. Selected file available in component state.");
      }
    } catch (err) {
      console.error("onUpload callback error", err);
      // let caller handle error as well; show a message
      message.error(err?.message || "Upload callback failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Dragger
        name={name}
        multiple={false}
        beforeUpload={beforeUpload}
        fileList={fileList}
        onRemove={handleRemove}
        accept={acceptStr}
        showUploadList={{
          showRemoveIcon: true,
          showDownloadIcon: false,
        }}
        style={{ padding: 12 }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to select</p>
        <p className="ant-upload-hint">
          Only one file at a time. Allowed: {allowedFormats.join(", ")} — max {(maxFileBytes / 1024 / 1024).toFixed(2)} MB.
        </p>
      </Dragger>

      <Space direction="vertical" size="small" style={{ marginTop: 12 }}>
        {selectedFile && (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Text strong>{selectedFile.name}</Text>
            <Text type="secondary">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</Text>
          </div>
        )}

        <Space>
          <Button
            type="primary"
            disabled={!selectedFile || uploading}
            loading={uploading}
            onClick={handleStartUpload}
          >
            {uploading ? `Uploading...` : uploadButtonText}
          </Button>

          <Button disabled={!selectedFile || uploading} onClick={() => handleRemove(selectedFile)}>
            Remove
          </Button>
        </Space>
      </Space>
    </div>
  );
}
